/* eslint-disable no-console */
/**
 * Analytics service for tracking events with Amplitude
 *
 * Note: Amplitude SDK needs to be installed and initialized before using this service.
 * Install with: npm install @amplitude/analytics-browser
 *
 * To initialize Amplitude, add to your main.js or initialization code:
 * import * as amplitude from '@amplitude/analytics-browser';
 * amplitude.init(process.env.VUE_APP_AMPLITUDE_API_KEY);
 */

import { analyticsEventNames } from './analyticsEventNames.enum';

class AnalyticsService {
  constructor() {
    this.amplitudeInstance = null;
    this.isEnabled = false;
    this.initializeAmplitude();
  }

  initializeAmplitude() {
    if (typeof window === 'undefined') {
      console.warn('[Analytics] Window is not available, tracking disabled');
      return;
    }

    if (window.amplitude) {
      this.amplitudeInstance = window.amplitude;
      this.isEnabled = true;
      console.log('[Analytics] Amplitude tracking enabled');
    } else {
      console.warn('[Analytics] Amplitude not initialized. Events will be logged to console only.');
      this.isEnabled = false;
    }
  }

  /**
   * Track an event with Amplitude
   * @param {string} eventName - Name of the event
   * @param {Object} eventProperties - Properties associated with the event
   */
  track(eventName, eventProperties = {}) {
    if (!eventName) {
      console.error('[Analytics] Event name is required');
      return;
    }

    // Lazy check: if Amplitude wasn't available during construction, check again now
    if (!this.isEnabled && typeof window !== 'undefined' && window.amplitude) {
      this.amplitudeInstance = window.amplitude;
      this.isEnabled = true;
    }

    // Send to Amplitude if initialized
    if (this.isEnabled && this.amplitudeInstance) {
      try {
        this.amplitudeInstance.track(eventName, eventProperties);
        console.log('[Analytics] Event tracked:', eventName);
      } catch (error) {
        console.error('[Analytics] Failed to track event:', error);
      }
    } else {
      console.warn('[Analytics] Event NOT sent to Amplitude (tracking disabled):', eventName);
    }
  }

  /**
   * Track a Measured Point Search event
   * @param {Object} eventData - Event data containing search parameters
   */
  trackMeasuredPointSearch(eventData) {
    this.track(analyticsEventNames.MEASURED_POINT_SEARCH, eventData);
  }

  /**
   * Track Measured Point Save from Filter Panel As event
   * @param {Object} eventData - Event data containing format type (csv/json/geo)
   */
  trackMeasuredPointSaveFromFilterPanelAs(eventData) {
    this.track(analyticsEventNames.DOWNLOAD_FILTERED_MEASURED_POINT_DATA_AS_FILE, eventData);
  }

  /**
   * Track Download Measured Points Data As File event from panel info
   * @param {Object} eventData - Event data containing format type (csv/json/geo)
   */
  trackDownloadMeasuredPointsDataAsFile(eventData) {
    this.track(analyticsEventNames.DOWNLOAD_MEASURED_POINTS_DATA_AS_FILE, eventData);
  }

  /**
   * Track Surface Filters Saved Format event
   * @param {Object} eventData - Event data containing format type and other export properties
   */
  trackSurfaceFiltersSavedFormat(eventData) {
    this.track(analyticsEventNames.SURFACE_FILTERS_SAVED_FORMAT, eventData);
  }

  /**
   * Track Surface Panel Info Saved Format event
   * @param {Object} eventData - Event data containing format type
   */
  trackSurfacePanelInfoSavedFormat(eventData) {
    this.track(analyticsEventNames.SURFACE_PANEL_INFO_SAVED_FORMAT, eventData);
  }

  /**
   * Track Surface Display Clicked Count event
   * @param {Object} eventData - Event data containing display click information
   */
  trackSurfaceDisplayClickedCount(eventData) {
    this.track(analyticsEventNames.SURFACE_DISPLAY_CLICKED_COUNT, eventData);
  }

  /**
   * Track Surfaces Log Filter Metadata event
   * @param {Object} eventData - Event data containing filter form field states
   */
  trackSurfacesLogFilterMetadata(eventData) {
    this.track(analyticsEventNames.SURFACES_LOG_FILTER_METADATA, eventData);
  }

  /**
   * Track Surface Detail Panel Opened event
   * @param {Object} eventData - Event data containing panel open information
   */
  trackSurfaceDetailPanelOpened(eventData) {
    this.track(analyticsEventNames.SURFACE_DETAIL_PANEL_OPENED, eventData);
  }

  /**
   * Track Measured Point Interaction event
   * @param {Object} eventData - Event data containing interaction information
   */
  trackMeasuredPointInteraction(eventData) {
    this.track(analyticsEventNames.MEASURED_POINT_INTERACTION, eventData);
  }
}

// Export singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;
